import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Search, Plus, Edit, Trash2, Eye, X, Calendar, Package, ClipboardList, User, Mail, FileText, Download, Link2, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminJobOrdersPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [jobOrders, setJobOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [dueDateFrom, setDueDateFrom] = useState('');
  const [dueDateTo, setDueDateTo] = useState('');
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  
  // Auto-fill states
  const [linkedOrderId, setLinkedOrderId] = useState(null);
  const [linkedOrderData, setLinkedOrderData] = useState(null);
  const [existingJobOrder, setExistingJobOrder] = useState(null);
  const [loadingOrderData, setLoadingOrderData] = useState(false);
  
  // PDF download state
  const [downloadingPdf, setDownloadingPdf] = useState(null);
  
  // Initial form state
  const initialFormState = useMemo(() => ({
    name: '',
    email: '',
    date_created: new Date().toISOString().split('T')[0],
    items: [{ item_name: '', quantity: 1, size: '', color: '', gender: '' }],
    description: '',
    size: '',
    color: '',
    gender: '',
    quantities: '',
    delivery_due_date: '',
    other_specifications: '',
    authorized_signature: '',
    linked_order_id: null
  }), []);
  
  // Form state
  const [formData, setFormData] = useState(initialFormState);

  // Load job orders
  const loadJobOrders = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      if (dueDateFrom) params.append('due_date_from', dueDateFrom);
      if (dueDateTo) params.append('due_date_to', dueDateTo);
      
      const response = await axios.get(`${API_URL}/api/admin/job-orders?${params.toString()}`, {
        withCredentials: true
      });
      setJobOrders(response.data.job_orders || []);
    } catch (error) {
      toast.error('Failed to load job orders');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, dateFrom, dateTo, dueDateFrom, dueDateTo]);

  // Check for order_id in URL and auto-fill
  useEffect(() => {
    const orderId = searchParams.get('order_id');
    if (orderId) {
      setLinkedOrderId(orderId);
      fetchOrderForAutoFill(orderId);
    } else {
      loadJobOrders();
    }
  }, [searchParams, loadJobOrders]);

  // Fetch order data for auto-fill
  const fetchOrderForAutoFill = async (orderId) => {
    setLoadingOrderData(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/job-orders/from-order/${orderId}`, {
        withCredentials: true
      });
      
      const { job_order_data, existing_job_order, order } = response.data;
      
      setLinkedOrderData(order);
      setExistingJobOrder(existing_job_order);
      
      if (existing_job_order) {
        toast.info('A job order already exists for this order');
      } else {
        // Auto-fill form with order data
        setFormData(prev => ({
          ...prev,
          ...job_order_data,
          items: job_order_data.items?.length ? job_order_data.items : [{ item_name: '', quantity: 1, size: '', color: '', gender: '' }]
        }));
        setShowCreateModal(true);
        toast.success('Form auto-filled from order data');
      }
    } catch (error) {
      console.error('Failed to fetch order:', error);
      toast.error('Failed to load order data for auto-fill');
    } finally {
      setLoadingOrderData(false);
      loadJobOrders();
    }
  };

  const handleSearch = () => {
    setLoading(true);
    loadJobOrders();
  };

  const resetForm = useCallback(() => {
    setFormData(initialFormState);
    setLinkedOrderId(null);
    setLinkedOrderData(null);
    setExistingJobOrder(null);
  }, [initialFormState]);

  // Stable item handlers
  const addItem = useCallback(() => {
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, { item_name: '', quantity: 1, size: '', color: '', gender: '' }]
    }));
  }, []);

  const removeItem = useCallback((index) => {
    setFormData(prev => {
      if (prev.items.length > 1) {
        return {
          ...prev,
          items: prev.items.filter((_, i) => i !== index)
        };
      }
      return prev;
    });
  }, []);

  const updateItem = useCallback((index, field, value) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  }, []);

  // Stable field update handler
  const updateField = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    
    if (!formData.name) {
      toast.error('Name is required');
      return;
    }
    
    try {
      await axios.post(`${API_URL}/api/admin/job-orders`, formData, {
        withCredentials: true
      });
      toast.success('Job order created successfully');
      setShowCreateModal(false);
      resetForm();
      // Clear URL params
      navigate('/admin/dashboard/job-orders', { replace: true });
      loadJobOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create job order');
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    
    try {
      await axios.put(`${API_URL}/api/admin/job-orders/${selectedOrder.id}`, formData, {
        withCredentials: true
      });
      toast.success('Job order updated successfully');
      setShowEditModal(false);
      loadJobOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update job order');
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API_URL}/api/admin/job-orders/${selectedOrder.id}`, {
        withCredentials: true
      });
      toast.success('Job order deleted');
      setShowDeleteConfirm(false);
      setSelectedOrder(null);
      loadJobOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete job order');
    }
  };

  const handleDownloadPdf = async (order) => {
    setDownloadingPdf(order.id);
    try {
      const response = await axios.get(`${API_URL}/api/admin/job-orders/${order.id}/pdf`, {
        withCredentials: true,
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Temaruco-JobOrder-${order.name?.replace(/\s+/g, '-') || order.job_order_id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF downloaded successfully');
    } catch (error) {
      console.error('PDF download error:', error);
      toast.error('Failed to download PDF');
    } finally {
      setDownloadingPdf(null);
    }
  };

  const openEdit = (order) => {
    setSelectedOrder(order);
    setFormData({
      name: order.name || '',
      email: order.email || '',
      date_created: order.date_created || '',
      items: order.items?.length ? order.items : [{ item_name: '', quantity: 1, size: '', color: '', gender: '' }],
      description: order.description || '',
      size: order.size || '',
      color: order.color || '',
      gender: order.gender || '',
      quantities: order.quantities || '',
      delivery_due_date: order.delivery_due_date || '',
      other_specifications: order.other_specifications || '',
      authorized_signature: order.authorized_signature || '',
      linked_order_id: order.linked_order_id || null
    });
    setShowEditModal(true);
  };

  const openView = (order) => {
    setSelectedOrder(order);
    setShowViewModal(true);
  };

  if (loading && !showCreateModal) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  // Job Order Form Component - uses stable handlers to prevent cursor issues
  const JobOrderForm = ({ onSubmit, isEdit = false }) => (
    <form onSubmit={onSubmit} className="space-y-6 max-h-[70vh] overflow-y-auto p-1">
      {/* Linked Order Notice */}
      {formData.linked_order_id && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center gap-3">
          <Link2 className="text-blue-600" size={20} />
          <div>
            <p className="font-semibold text-blue-800">Linked to Order</p>
            <p className="text-sm text-blue-600 font-mono">{formData.linked_order_id}</p>
          </div>
        </div>
      )}
      
      {/* Basic Info */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">Client Name *</label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => updateField('name', e.target.value)}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:outline-none"
            required
            data-testid="job-order-name"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Email</label>
          <input
            type="email"
            value={formData.email}
            onChange={(e) => updateField('email', e.target.value)}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:outline-none"
            data-testid="job-order-email"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">Order Date</label>
          <input
            type="date"
            value={formData.date_created}
            onChange={(e) => updateField('date_created', e.target.value)}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:outline-none"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Delivery Due Date</label>
          <input
            type="date"
            value={formData.delivery_due_date}
            onChange={(e) => updateField('delivery_due_date', e.target.value)}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:outline-none"
            data-testid="job-order-due-date"
          />
        </div>
      </div>

      {/* Dynamic Items */}
      <div>
        <div className="flex justify-between items-center mb-2">
          <label className="block text-sm font-medium">Items</label>
          <Button type="button" variant="outline" size="sm" onClick={addItem} data-testid="add-item-btn">
            <Plus size={16} className="mr-1" /> Add Row
          </Button>
        </div>
        <div className="space-y-3 bg-zinc-50 p-3 rounded-lg">
          {formData.items.map((item, index) => (
            <div key={index} className="flex gap-2 items-start flex-wrap">
              <div className="flex-1 min-w-[200px]">
                <input
                  type="text"
                  placeholder="Item Name"
                  value={item.item_name}
                  onChange={(e) => updateItem(index, 'item_name', e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:outline-none text-sm"
                />
              </div>
              <div className="w-20">
                <input
                  type="number"
                  placeholder="Qty"
                  value={item.quantity}
                  onChange={(e) => updateItem(index, 'quantity', parseInt(e.target.value) || 0)}
                  className="w-full px-2 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:outline-none text-sm"
                  min="1"
                />
              </div>
              <div className="w-24">
                <input
                  type="text"
                  placeholder="Size"
                  value={item.size || ''}
                  onChange={(e) => updateItem(index, 'size', e.target.value)}
                  className="w-full px-2 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:outline-none text-sm"
                />
              </div>
              <div className="w-24">
                <input
                  type="text"
                  placeholder="Color"
                  value={item.color || ''}
                  onChange={(e) => updateItem(index, 'color', e.target.value)}
                  className="w-full px-2 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:outline-none text-sm"
                />
              </div>
              <div className="w-24">
                <select
                  value={item.gender || ''}
                  onChange={(e) => updateItem(index, 'gender', e.target.value)}
                  className="w-full px-2 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:outline-none text-sm"
                >
                  <option value="">Gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Child">Child</option>
                  <option value="Unisex">Unisex</option>
                </select>
              </div>
              {formData.items.length > 1 && (
                <button 
                  type="button" 
                  onClick={() => removeItem(index)}
                  className="p-2 text-red-500 hover:bg-red-50 rounded"
                >
                  <X size={16} />
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Description */}
      <div>
        <label className="block text-sm font-medium mb-1">Description</label>
        <textarea
          value={formData.description}
          onChange={(e) => updateField('description', e.target.value)}
          className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:outline-none"
          rows="3"
          data-testid="job-order-description"
        />
      </div>

      {/* Summary Specifications */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">Sizes Summary</label>
          <input
            type="text"
            value={formData.size}
            onChange={(e) => updateField('size', e.target.value)}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:outline-none"
            placeholder="e.g., S, M, L"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Colors Summary</label>
          <input
            type="text"
            value={formData.color}
            onChange={(e) => updateField('color', e.target.value)}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:outline-none"
            placeholder="e.g., Black, White"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Gender Summary</label>
          <select
            value={formData.gender}
            onChange={(e) => updateField('gender', e.target.value)}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:outline-none"
            data-testid="job-order-gender"
          >
            <option value="">Select Gender</option>
            <option value="Male">Male</option>
            <option value="Female">Female</option>
            <option value="Child">Child</option>
            <option value="Unisex">Unisex</option>
            <option value="Mixed">Mixed</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Total Quantities</label>
          <input
            type="text"
            value={formData.quantities}
            onChange={(e) => updateField('quantities', e.target.value)}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:outline-none"
            placeholder="e.g., 100"
          />
        </div>
      </div>

      {/* Other Specifications */}
      <div>
        <label className="block text-sm font-medium mb-1">Other Specifications</label>
        <textarea
          value={formData.other_specifications}
          onChange={(e) => updateField('other_specifications', e.target.value)}
          className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:outline-none"
          rows="2"
          placeholder="Any additional production notes..."
        />
      </div>

      {/* Authorized Signature */}
      <div>
        <label className="block text-sm font-medium mb-1">Authorized Signature</label>
        <input
          type="text"
          value={formData.authorized_signature}
          onChange={(e) => updateField('authorized_signature', e.target.value)}
          className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:outline-none"
          placeholder="Enter authorized signature"
          data-testid="job-order-signature"
        />
      </div>

      <div className="flex gap-3 pt-4 border-t">
        <Button type="submit" className="flex-1" data-testid="submit-job-order">
          {isEdit ? 'Update Job Order' : 'Create Job Order'}
        </Button>
        <Button 
          type="button" 
          variant="outline" 
          onClick={() => {
            if (isEdit) {
              setShowEditModal(false);
            } else {
              setShowCreateModal(false);
              resetForm();
              navigate('/admin/dashboard/job-orders', { replace: true });
            }
          }}
        >
          Cancel
        </Button>
      </div>
    </form>
  );

  return (
    <div className="space-y-6">
      {/* Loading overlay for order fetch */}
      {loadingOrderData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-xl">
            <div className="loading-spinner mx-auto mb-4"></div>
            <p className="text-center">Loading order data...</p>
          </div>
        </div>
      )}
      
      {/* Existing Job Order Warning */}
      {existingJobOrder && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="text-amber-600 flex-shrink-0 mt-0.5" size={20} />
          <div>
            <p className="font-semibold text-amber-800">Job Order Already Exists</p>
            <p className="text-sm text-amber-700 mt-1">
              A job order (<span className="font-mono font-semibold">{existingJobOrder.job_order_id}</span>) already exists for order {linkedOrderId}.
            </p>
            <div className="flex gap-2 mt-3">
              <Button size="sm" onClick={() => openView(existingJobOrder)}>
                <Eye size={16} className="mr-1" /> View Job Order
              </Button>
              <Button size="sm" variant="outline" onClick={() => {
                setExistingJobOrder(null);
                setLinkedOrderId(null);
                navigate('/admin/dashboard/job-orders', { replace: true });
              }}>
                Dismiss
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-oswald text-3xl font-bold uppercase" data-testid="job-orders-title">
            Job Orders
          </h1>
          <p className="text-zinc-600 mt-1">Manage internal job orders and production requests</p>
        </div>
        <Button onClick={() => { resetForm(); setShowCreateModal(true); }} data-testid="create-job-order-btn">
          <Plus size={18} className="mr-2" /> Create Job Order
        </Button>
      </div>

      {/* Search & Filters */}
      <div className="bg-white rounded-lg shadow-sm p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <div className="md:col-span-2 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-zinc-400" size={20} />
            <input
              type="text"
              placeholder="Search by name, email, job ID, or order ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              className="w-full pl-10 pr-4 py-2 border border-zinc-300 rounded-lg focus:ring-2 focus:ring-primary focus:outline-none"
              data-testid="search-job-orders"
            />
          </div>
          <div>
            <label className="block text-xs text-zinc-500 mb-1">Date From</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="w-full px-3 py-2 border border-zinc-300 rounded-lg"
            />
          </div>
          <div>
            <label className="block text-xs text-zinc-500 mb-1">Date To</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="w-full px-3 py-2 border border-zinc-300 rounded-lg"
            />
          </div>
          <div>
            <label className="block text-xs text-zinc-500 mb-1">Due From</label>
            <input
              type="date"
              value={dueDateFrom}
              onChange={(e) => setDueDateFrom(e.target.value)}
              className="w-full px-3 py-2 border border-zinc-300 rounded-lg"
            />
          </div>
          <div>
            <label className="block text-xs text-zinc-500 mb-1">Due To</label>
            <input
              type="date"
              value={dueDateTo}
              onChange={(e) => setDueDateTo(e.target.value)}
              className="w-full px-3 py-2 border border-zinc-300 rounded-lg"
            />
          </div>
        </div>
        <div className="mt-4">
          <Button onClick={handleSearch} data-testid="search-btn">Search</Button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1000px]">
            <thead className="bg-zinc-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Job Order ID</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Linked Order</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Client Name</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Email</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Items</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Due Date</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Created</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y" data-testid="job-orders-table">
              {jobOrders.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-4 py-12 text-center">
                    <ClipboardList className="mx-auto text-zinc-400 mb-4" size={48} />
                    <p className="text-zinc-600">No job orders found</p>
                  </td>
                </tr>
              ) : (
                jobOrders.map((order) => (
                  <tr key={order.id} className="hover:bg-zinc-50">
                    <td className="px-4 py-3 font-medium text-[#D90429]">{order.job_order_id}</td>
                    <td className="px-4 py-3 text-sm">
                      {order.linked_order_id ? (
                        <span className="text-blue-600 font-mono text-xs bg-blue-50 px-2 py-1 rounded">
                          {order.linked_order_id}
                        </span>
                      ) : (
                        <span className="text-zinc-400">Manual</span>
                      )}
                    </td>
                    <td className="px-4 py-3">{order.name}</td>
                    <td className="px-4 py-3 text-sm text-zinc-600">{order.email || '-'}</td>
                    <td className="px-4 py-3 text-sm">
                      {order.items?.length || 0} item(s)
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {order.delivery_due_date ? new Date(order.delivery_due_date).toLocaleDateString() : '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-zinc-500">
                      {new Date(order.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1">
                        <button
                          onClick={() => openView(order)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                          title="View"
                          data-testid={`view-${order.id}`}
                        >
                          <Eye size={16} />
                        </button>
                        <button
                          onClick={() => handleDownloadPdf(order)}
                          className="p-2 text-green-600 hover:bg-green-50 rounded"
                          title="Download PDF"
                          disabled={downloadingPdf === order.id}
                          data-testid={`pdf-${order.id}`}
                        >
                          {downloadingPdf === order.id ? (
                            <div className="w-4 h-4 border-2 border-green-600 border-t-transparent rounded-full animate-spin"></div>
                          ) : (
                            <Download size={16} />
                          )}
                        </button>
                        <button
                          onClick={() => openEdit(order)}
                          className="p-2 text-yellow-600 hover:bg-yellow-50 rounded"
                          title="Edit"
                          data-testid={`edit-${order.id}`}
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          onClick={() => { setSelectedOrder(order); setShowDeleteConfirm(true); }}
                          className="p-2 text-red-600 hover:bg-red-50 rounded"
                          title="Delete"
                          data-testid={`delete-${order.id}`}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-xl max-w-3xl w-full p-6 my-8 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h3 className="text-xl font-bold">Create Job Order</h3>
                {linkedOrderId && (
                  <p className="text-sm text-blue-600 mt-1">Auto-filled from order: {linkedOrderId}</p>
                )}
              </div>
              <button onClick={() => { setShowCreateModal(false); resetForm(); navigate('/admin/dashboard/job-orders', { replace: true }); }} className="text-zinc-400 hover:text-zinc-600">
                <X size={24} />
              </button>
            </div>
            <JobOrderForm onSubmit={handleCreate} />
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-xl max-w-3xl w-full p-6 my-8 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold">Edit Job Order</h3>
              <button onClick={() => setShowEditModal(false)} className="text-zinc-400 hover:text-zinc-600">
                <X size={24} />
              </button>
            </div>
            <JobOrderForm onSubmit={handleUpdate} isEdit />
          </div>
        </div>
      )}

      {/* View Modal */}
      {showViewModal && selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-xl max-w-3xl w-full p-6 max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold">Job Order Details</h3>
              <button onClick={() => setShowViewModal(false)} className="text-zinc-400 hover:text-zinc-600">
                <X size={24} />
              </button>
            </div>
            
            <div className="space-y-6">
              <div className="bg-zinc-50 p-4 rounded-lg flex justify-between items-start">
                <div>
                  <p className="text-2xl font-bold text-[#D90429]">{selectedOrder.job_order_id}</p>
                  <p className="text-sm text-zinc-500">Created: {new Date(selectedOrder.created_at).toLocaleString()}</p>
                </div>
                {selectedOrder.linked_order_id && (
                  <div className="text-right">
                    <p className="text-xs text-zinc-500">Linked Order</p>
                    <p className="font-mono text-sm text-blue-600 bg-blue-50 px-2 py-1 rounded">
                      {selectedOrder.linked_order_id}
                    </p>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-2">
                  <User className="text-zinc-400" size={18} />
                  <div>
                    <p className="text-xs text-zinc-500">Client Name</p>
                    <p className="font-medium">{selectedOrder.name}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="text-zinc-400" size={18} />
                  <div>
                    <p className="text-xs text-zinc-500">Email</p>
                    <p className="font-medium">{selectedOrder.email || '-'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="text-zinc-400" size={18} />
                  <div>
                    <p className="text-xs text-zinc-500">Order Date</p>
                    <p className="font-medium">{selectedOrder.date_created}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="text-zinc-400" size={18} />
                  <div>
                    <p className="text-xs text-zinc-500">Delivery Due</p>
                    <p className="font-medium">{selectedOrder.delivery_due_date || '-'}</p>
                  </div>
                </div>
              </div>

              {/* Items */}
              {selectedOrder.items?.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-2 flex items-center gap-2">
                    <Package size={18} /> Items
                  </h4>
                  <table className="w-full border rounded-lg overflow-hidden">
                    <thead className="bg-zinc-100">
                      <tr>
                        <th className="px-4 py-2 text-left text-sm">Item Name</th>
                        <th className="px-4 py-2 text-left text-sm">Size</th>
                        <th className="px-4 py-2 text-left text-sm">Color</th>
                        <th className="px-4 py-2 text-left text-sm">Gender</th>
                        <th className="px-4 py-2 text-left text-sm">Qty</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {selectedOrder.items.map((item, i) => (
                        <tr key={i}>
                          <td className="px-4 py-2">{item.item_name}</td>
                          <td className="px-4 py-2">{item.size || '-'}</td>
                          <td className="px-4 py-2">{item.color || '-'}</td>
                          <td className="px-4 py-2">{item.gender || '-'}</td>
                          <td className="px-4 py-2">{item.quantity}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Specifications */}
              <div className="grid grid-cols-4 gap-4 bg-zinc-50 p-4 rounded-lg">
                <div>
                  <p className="text-xs text-zinc-500">Sizes</p>
                  <p className="font-medium">{selectedOrder.size || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-zinc-500">Colors</p>
                  <p className="font-medium">{selectedOrder.color || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-zinc-500">Gender</p>
                  <p className="font-medium">{selectedOrder.gender || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-zinc-500">Total Qty</p>
                  <p className="font-medium">{selectedOrder.quantities || '-'}</p>
                </div>
              </div>

              {selectedOrder.description && (
                <div>
                  <h4 className="font-semibold mb-2 flex items-center gap-2">
                    <FileText size={18} /> Description
                  </h4>
                  <p className="text-zinc-700 bg-zinc-50 p-3 rounded-lg">{selectedOrder.description}</p>
                </div>
              )}

              {selectedOrder.other_specifications && (
                <div>
                  <h4 className="font-semibold mb-2">Other Specifications</h4>
                  <p className="text-zinc-700 bg-zinc-50 p-3 rounded-lg">{selectedOrder.other_specifications}</p>
                </div>
              )}

              {selectedOrder.authorized_signature && (
                <div>
                  <h4 className="font-semibold mb-2">Authorized Signature</h4>
                  <p className="text-zinc-700 italic">{selectedOrder.authorized_signature}</p>
                </div>
              )}

              <div className="flex gap-3 pt-4 border-t">
                <Button onClick={() => handleDownloadPdf(selectedOrder)} disabled={downloadingPdf === selectedOrder.id}>
                  {downloadingPdf === selectedOrder.id ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                      Generating...
                    </>
                  ) : (
                    <>
                      <Download size={16} className="mr-2" /> Download PDF
                    </>
                  )}
                </Button>
                <Button variant="outline" onClick={() => openEdit(selectedOrder)}>
                  <Edit size={16} className="mr-2" /> Edit
                </Button>
                <Button variant="outline" onClick={() => setShowViewModal(false)}>Close</Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {showDeleteConfirm && selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-xl max-w-md w-full p-6 my-8 max-h-[90vh] overflow-y-auto">
            <div className="text-center">
              <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                <Trash2 className="text-red-600" size={32} />
              </div>
              <h3 className="text-xl font-bold mb-2">Delete Job Order?</h3>
              <p className="text-zinc-600 mb-6">
                Are you sure you want to delete job order "{selectedOrder.job_order_id}"? This action cannot be undone.
              </p>
              <div className="flex gap-3">
                <Button
                  onClick={handleDelete}
                  className="flex-1 bg-red-600 hover:bg-red-700"
                  data-testid="confirm-delete-btn"
                >
                  Yes, Delete
                </Button>
                <Button
                  variant="outline"
                  onClick={() => { setShowDeleteConfirm(false); setSelectedOrder(null); }}
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminJobOrdersPage;
